import { useAppDispatch, useAppSelector } from 'app/hooks';
import store from 'app/store';
import RouteErrorFallback from 'components/common/route-error-fallback';
import { SYSTEM_SELECTOR_ADMIN_ROLE } from 'data/roles';
import { checkUserAuth, fetchSystemSelectorZoomUrl } from 'features/auth';
import ExistingEquipmentImagesPage from 'pages/upload/existing-equipment-images';
import PublicImagesPage from 'pages/upload/structure-images';
import { useEffect } from 'react';
import * as ReactDOM from 'react-dom/client';
import { Provider as ReduxProvider } from 'react-redux';
import { createBrowserRouter, Outlet, RouterProvider } from 'react-router-dom';
import AdminApp from 'routes/admin';
import MainApp from 'routes/main';
import 'styles/main.css';
import { installConsoleRecorder } from 'utils/error-log/console-recorder';
import { installGlobalErrorHandlers } from 'utils/error-log/install-global-error-handlers';
import { installInteractionRecorder } from 'utils/error-log/interaction-recorder';

installConsoleRecorder();
installInteractionRecorder();
installGlobalErrorHandlers();

function PrivateRoute({
  requiredRole,
  requiredPermission
}: {
  requiredRole?: string;
  requiredPermission?: string;
}) {
  const { isAuthenticated, isLoading, user } = useAppSelector(
    (state) => state.auth
  );

  if (isLoading) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated) {
    const redirectUrl = window.location.href;
    window.location.href = `${process.env.REACT_APP_PARTNERNET_URL}/partnernet/security/logon.cfm?redirectUrl=${redirectUrl}`;
    return null;
  }

  if (requiredPermission && !user?.permissions.includes(requiredPermission)) {
    return <div>Not authorized</div>;
  }

  if (requiredRole && !user?.roles.includes(requiredRole)) {
    return <div>Not authorized</div>;
  }

  return <Outlet />;
}

const router = createBrowserRouter([
  {
    errorElement: <RouteErrorFallback />,
    children: [
      {
        path: '/',
        element: <PrivateRoute />,
        children: [
          {
            path: '/',
            element: <MainApp />
          },
          {
            path: '/admin',
            element: <PrivateRoute requiredRole={SYSTEM_SELECTOR_ADMIN_ROLE} />,
            children: [
              {
                path: '/admin',
                element: <AdminApp />
              }
            ]
          }
        ]
      },
      {
        path: '/structure-images',
        element: <PublicImagesPage />
      },
      {
        path: '/existing-equipment-images',
        element: <ExistingEquipmentImagesPage />
      }
    ]
  }
]);

function App() {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  useEffect(() => {
    dispatch(checkUserAuth());
  }, [dispatch]);

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchSystemSelectorZoomUrl());
    }
  }, [dispatch, isAuthenticated]);

  return <RouterProvider router={router} />;
}

const rootElement = document.getElementById('root');
if (rootElement) {
  const root = ReactDOM.createRoot(rootElement);
  root.render(
    <ReduxProvider store={store}>
      <App />
    </ReduxProvider>
  );
}
